import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoChatbubbleEllipses, IoPencil, IoVolumeHigh, IoVolumeMute } from "react-icons/io5";
import { supabase } from "../../supabaseClient";
import { usePublicUser } from "./data/usePublicUserID";
import { useChatRooms } from "./data/useChatRooms";
import { useMessages } from "./data/useMessages";
import renderMessageContent from "./Emoji-img";
import "./FloatingMessenger.css";

const lastReadKey = (userId) => `fb-last-read-chat-map-${userId}`;

const FloatingMessenger = () => {
  const { publicUser, loading: userLoading } = usePublicUser();
  const currentUserId = publicUser?.public_user_id || null;
  const { chatRooms, loading: roomsLoading, refresh } = useChatRooms(currentUserId);
  const [isOpen, setIsOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState(null);
  const [draft, setDraft] = useState("");
  const [isRenamingChat, setIsRenamingChat] = useState(false);
  const [chatNameDraft, setChatNameDraft] = useState("");
  const [roomFilter, setRoomFilter] = useState("all");
  const [lastReadByChat, setLastReadByChat] = useState({});
  const [activityVersion, setActivityVersion] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const chatRoomIdsRef = useRef(new Set());
  const audioContextRef = useRef(null);
  const crossbarAudioRef = useRef(null);
  const hasCrossbarAudioRef = useRef(false);
  const soundEnabledRef = useRef(false);
  const messagesContainerRef = useRef(null);
  const { messages, loading: messagesLoading, sendMessage } = useMessages(activeChatId);

  const enableNotificationSound = () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return Promise.resolve(false);

    if (!audioContextRef.current) audioContextRef.current = new AudioContext();
    return audioContextRef.current.resume()
      .then(() => {
        soundEnabledRef.current = true;
        setSoundEnabled(true);
        return true;
      })
      .catch(() => {
        soundEnabledRef.current = false;
        setSoundEnabled(false);
        return false;
      });
  };

  const toggleNotificationSound = async () => {
    if (soundEnabledRef.current) {
      soundEnabledRef.current = false;
      setSoundEnabled(false);
      return;
    }

    if (await enableNotificationSound()) playCrossbarSound();
  };

  const playCrossbarSound = () => {
    const audioContext = audioContextRef.current;
    if (!soundEnabledRef.current || !audioContext || audioContext.state !== "running") return;

    if (hasCrossbarAudioRef.current && crossbarAudioRef.current) {
      crossbarAudioRef.current.currentTime = 0;
      crossbarAudioRef.current.play().catch(() => {});
      return;
    }

    const now = audioContext.currentTime;
    const masterGain = audioContext.createGain();
    masterGain.gain.setValueAtTime(0.6, now);
    masterGain.connect(audioContext.destination);

    // The brief noise burst is the puck impact; inharmonic partials create the bar ring.
    const noiseBuffer = audioContext.createBuffer(1, audioContext.sampleRate * 0.035, audioContext.sampleRate);
    const noiseData = noiseBuffer.getChannelData(0);
    noiseData.forEach((_, index) => {
      noiseData[index] = (Math.random() * 2 - 1) * (1 - index / noiseData.length);
    });

    const impact = audioContext.createBufferSource();
    const impactFilter = audioContext.createBiquadFilter();
    const impactGain = audioContext.createGain();
    impact.buffer = noiseBuffer;
    impactFilter.type = "bandpass";
    impactFilter.frequency.setValueAtTime(2400, now);
    impactFilter.Q.setValueAtTime(2.5, now);
    impactGain.gain.setValueAtTime(0.8, now);
    impactGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
    impact.connect(impactFilter).connect(impactGain).connect(masterGain);
    impact.start(now);

    [1760, 2530, 3370].forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, now);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.94, now + 0.58 + index * 0.05);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.34 / (index + 1), now + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.52 + index * 0.06);
      oscillator.connect(gain).connect(masterGain);
      oscillator.start(now);
      oscillator.stop(now + 0.7);
    });
  };

  useEffect(() => {
    chatRoomIdsRef.current = new Set(chatRooms.map((room) => room.id));
  }, [chatRooms]);

  useEffect(() => {
    const unlockSound = () => enableNotificationSound();
    window.addEventListener("pointerdown", unlockSound, { once: true });
    window.addEventListener("keydown", unlockSound, { once: true });

    return () => {
      window.removeEventListener("pointerdown", unlockSound);
      window.removeEventListener("keydown", unlockSound);
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) return;
    try {
      setLastReadByChat(JSON.parse(localStorage.getItem(lastReadKey(currentUserId)) || "{}"));
    } catch (error) {
      console.error("Failed to load floating messenger read state:", error);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) return undefined;

    const channel = supabase
      .channel(`floating-messenger-${currentUserId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages" },
        (payload) => {
          const message = payload.new;
          if (!chatRoomIdsRef.current.has(message.chat_id)) return;
          setActivityVersion((version) => version + 1);
          refresh();
          if (message.sender_id !== currentUserId && !message.content?.startsWith("[system]")) {
            playCrossbarSound();
          }
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [currentUserId, refresh]);

  const unreadRooms = useMemo(() => chatRooms.filter((room) => {
    const latest = room.latestMessage;
    return Boolean(
      latest?.created_at &&
      latest.created_at > (lastReadByChat[room.id] || "") &&
      latest.sender_id !== currentUserId &&
      !latest.content?.startsWith("[system]")
    );
  }), [chatRooms, currentUserId, lastReadByChat, activityVersion]);

  const visibleRooms = roomFilter === "unread" ? unreadRooms : chatRooms;

  const activeRoom = chatRooms.find((room) => room.id === activeChatId) || null;

  const markRead = (roomId, latestAt) => {
    if (!latestAt || !currentUserId) return;
    const next = { ...lastReadByChat, [roomId]: latestAt };
    setLastReadByChat(next);
    localStorage.setItem(lastReadKey(currentUserId), JSON.stringify(next));
  };

  const openChat = (room) => {
    setActiveChatId(room.id);
    setIsOpen(true);
    markRead(room.id, room.latestMessage?.created_at);
  };

  const renameChat = async (event) => {
    event.preventDefault();
    const name = chatNameDraft.trim();
    if (!activeChatId || !name) return;

    const { error } = await supabase
      .from("chat_rooms")
      .update({ name })
      .eq("id", activeChatId);

    if (error) {
      console.error("Unable to rename chat:", error);
      return;
    }

    setIsRenamingChat(false);
    await refresh();
  };

  useEffect(() => {
    if (!isOpen || !activeChatId || messagesLoading) return;

    const frameId = window.requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [activeChatId, isOpen, messages, messagesLoading]);

  const handleSend = async (event) => {
    event.preventDefault();
    if (!draft.trim() || !activeChatId || !currentUserId) return;
    await sendMessage(currentUserId, draft.trim());
    setDraft("");
    await refresh();
  };

  if (userLoading || !currentUserId) return null;

  return (
    <aside className={`floating-messenger ${isOpen ? "is-open" : ""}`} aria-label="Messages">
      <audio
        ref={crossbarAudioRef}
        preload="auto"
        src="/audio/crossbar-hit.mp3"
        onCanPlayThrough={() => { hasCrossbarAudioRef.current = true; }}
        onError={() => { hasCrossbarAudioRef.current = false; }}
      />
      {isOpen && (
        <div className="floating-messenger__panel">
          <div className={`floating-messenger__bar ${isRenamingChat ? "is-renaming" : ""}`}>
            {isRenamingChat ? (
              <form className="floating-messenger__rename" onSubmit={renameChat}>
                <input
                  autoFocus
                  value={chatNameDraft}
                  maxLength={80}
                  onChange={(event) => setChatNameDraft(event.target.value)}
                  aria-label="Chat name"
                />
                <button type="submit" className="floating-messenger__rename-action">Save</button>
                <button
                  type="button"
                  className="floating-messenger__rename-action"
                  onClick={() => setIsRenamingChat(false)}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <div className="floating-messenger__title">
                <div className="floating-messenger__title-text">
                  <strong>{activeRoom ? activeRoom.name : "Messages"}</strong>
                  {activeRoom && activeRoom.participants?.length > 0 && (
                    <span className="floating-messenger__participants">
                      {activeRoom.participants.map((participant) => participant.username).join(", ")}
                    </span>
                  )}
                </div>
                {activeRoom && (
                  <button
                    type="button"
                    className="floating-messenger__rename-button"
                    onClick={() => {
                      setChatNameDraft(activeRoom.name || "");
                      setIsRenamingChat(true);
                    }}
                    title="Rename chat"
                    aria-label="Rename chat"
                  >
                    <IoPencil />
                  </button>
                )}
              </div>
            )}
            <div className="floating-messenger__bar-actions">
              <button
                type="button"
                className={`floating-messenger__sound ${soundEnabled ? "is-enabled" : ""}`}
                onClick={toggleNotificationSound}
                title={soundEnabled ? "Turn message sounds off" : "Turn message sounds on"}
                aria-label={soundEnabled ? "Turn message sounds off" : "Turn message sounds on"}
                aria-pressed={soundEnabled}
              >
                {soundEnabled ? <IoVolumeHigh /> : <IoVolumeMute />}
              </button>
              <button type="button" onClick={() => setIsOpen(false)} aria-label="Close messages">×</button>
            </div>
          </div>

          {!activeChatId ? (
            <div className="floating-messenger__rooms">
              <div className="floating-messenger__filters" role="tablist" aria-label="Message filters">
                <button
                  type="button"
                  className={roomFilter === "all" ? "is-active" : ""}
                  onClick={() => setRoomFilter("all")}
                  role="tab"
                  aria-selected={roomFilter === "all"}
                >
                  All chats
                </button>
                <button
                  type="button"
                  className={roomFilter === "unread" ? "is-active" : ""}
                  onClick={() => setRoomFilter("unread")}
                  role="tab"
                  aria-selected={roomFilter === "unread"}
                >
                  Unread {unreadRooms.length > 0 && <span>{unreadRooms.length}</span>}
                </button>
              </div>
              {roomsLoading && <p className="floating-messenger__empty">Loading chats...</p>}
              {!roomsLoading && visibleRooms.length === 0 && (
                <p className="floating-messenger__empty">
                  {roomFilter === "unread" ? "No unread messages" : "No chats yet"}
                </p>
              )}
              {visibleRooms.map((room) => (
                <button
                  type="button"
                  className={`floating-messenger__room ${unreadRooms.includes(room) ? "is-unread" : ""}`}
                  key={room.id}
                  onClick={() => openChat(room)}
                >
                  <span>{room.name || room.participants.map((participant) => participant.username).join(", ")}</span>
                  <small>{room.latestMessage?.content?.replace(/^\[system\]/, "")}</small>
                </button>
              ))}
            </div>
          ) : (
            <>
              <button type="button" className="floating-messenger__back" onClick={() => setActiveChatId(null)}>
                ← All unread chats
              </button>
              <div className="floating-messenger__messages" ref={messagesContainerRef}>
                {messagesLoading && <p className="floating-messenger__empty">Loading messages...</p>}
                {messages.map((message) => (
                  <div className={`floating-messenger__message ${message.sender_id === currentUserId ? "is-own" : ""}`} key={message.id}>
                    {!message.isSystem && <b>{message.senderName}</b>}
                    <span>{renderMessageContent(message.content.replace(/^\[system\]/, ""))}</span>
                  </div>
                ))}
              </div>
              <form className="floating-messenger__composer" onSubmit={handleSend}>
                <input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="Reply..."
                  aria-label="Reply to chat"
                />
                <button type="submit" aria-label="Send reply">→</button>
              </form>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="floating-messenger__trigger"
        onClick={() => {
          enableNotificationSound();
          setIsOpen((open) => !open);
        }}
        aria-label={isOpen ? "Close messages" : "Open messages"}
      >
        <IoChatbubbleEllipses aria-hidden="true" />
        {unreadRooms.length > 0 && <span className="floating-messenger__dot" aria-label={`${unreadRooms.length} unread chats`} />}
      </button>
    </aside>
  );
};

export default FloatingMessenger;
